import React, { PureComponent } from 'react';
import { Currency } from '~/Api/Currency';
import ProductPriceSelectPopover from '~/ProductPriceSelectPopover';
import Button, { Link } from '~/UI/Button';
import buttonIconStyles from '~/UI/Button/icon.less';
import Icon from '~/UI/Icon/Icon';
import LayoutConstraint from '~/UI/LayoutConstraint/LayoutConstraint';

import styles from './styles.less';

export interface SortFields {
	country: string;
	brandName: string;
	marketingAuthorizationHolder: string;
	atc: string;
	activeSubstance: string;
	dosageForm: string;
	dosageStrength: string;
	packageSize: string;
}

interface Props {
	sortField: string | null;
	sortFields: SortFields | null;
	orderDirection: string | null;
	sortLink(a: string, b: string): { pathname: string; search: string };
	accessoryHeader: React.ReactNode;
	priceHeader: React.ReactNode;
	currency: string | null;
	currencies: Currency[] | null;
	priceType: string | null;
	filterCalculatedPrice: boolean;
	style: any;
	onPriceChange(a: string | null, b: string | null, c: boolean): void;
}

interface State {
	showPriceSelect: boolean;
}

export default class Header extends PureComponent<Props, State> {
	static defaultProps = {
		priceHeader: 'Price',
	};

	state = {
		showPriceSelect: false,
	};

	pricePopoverContainerElement: HTMLSpanElement | null = null;

	renderCell(fieldName: string | undefined, content: string, className: string) {
		const { sortField, orderDirection, sortLink } = this.props;
		if (!fieldName || sortLink === null) {
			return (
				<div className={className}>
					<div className={styles.cellContent}>{content}</div>
				</div>
			);
		}

		const isSortedBy = sortField === fieldName;

		return (
			<div className={className}>
				<Link
					data-testid={`product-table-sort-link-${fieldName}`}
					styles={{ button: styles.sortLink }}
					to={sortLink(fieldName, isSortedBy && orderDirection === 'desc' ? 'asc' : 'desc')}
				>
					{content}
					<span className={styles.sortIcon}>
						<Icon
							name={
								isSortedBy ? (orderDirection === 'asc' ? 'triangle-up' : 'triangle-down') : 'triangle-down-transparent'
							}
						/>
					</span>
				</Link>
			</div>
		);
	}

	handleClosePriceSelectPopover = (event: Event) => {
		if (
			event.target !== this.pricePopoverContainerElement &&
			// @ts-expect-error Argument of type 'EventTarget | null' is not assignable to parameter of type 'Node | null'.
			!this.pricePopoverContainerElement?.contains(event.target)
		) {
			this.setState({ showPriceSelect: false });
		}
	};

	handlePriceChange = (code: string | null, type: string | null, filterCalculatedPrice: boolean) => {
		this.setState({ showPriceSelect: false });
		this.props.onPriceChange(code, type, filterCalculatedPrice);
	};

	showPriceSelect() {
		this.setState({ showPriceSelect: true });
	}

	render() {
		const {
			props: {
				sortFields,
				accessoryHeader,
				priceHeader,
				currency,
				currencies,
				priceType,
				filterCalculatedPrice,
				style,
			},
			state: { showPriceSelect },
		} = this;

		const priceButton = (
			<Button
				data-testid='product-table-price-button'
				styles={buttonIconStyles}
				onClick={() => this.setState({ showPriceSelect: !showPriceSelect })}
			>
				<Icon name='currency'>price and currency</Icon>
			</Button>
		);

		return (
			<div className={styles.header} style={style}>
				<div className={styles.addOrRemoveCell}>{accessoryHeader}</div>
				{this.renderCell(sortFields?.country, 'Country', styles.countryCell)}
				{this.renderCell(sortFields?.brandName, 'Brand name', styles.brandCell)}
				{this.renderCell(sortFields?.marketingAuthorizationHolder, 'Company', styles.mahCell)}
				{this.renderCell(sortFields?.atc, 'ATC', styles.atcCell)}
				{this.renderCell(sortFields?.activeSubstance, 'Active Substance', styles.activeSubstanceCell)}
				{this.renderCell(sortFields?.dosageForm, 'Dosage Form', styles.dosageCell)}
				{this.renderCell(sortFields?.dosageStrength, 'Dosage strength', styles.dosageStrengthCell)}
				{this.renderCell(sortFields?.packageSize, 'Package', styles.packageCell)}
				<div className={styles.priceCell}>{priceHeader}</div>
				<div className={styles.priceTypeCell}>
					<span ref={(ref) => (this.pricePopoverContainerElement = ref)}>
						<LayoutConstraint reference={priceButton}>
							{({ referenceRect }) =>
								showPriceSelect ? (
									<ProductPriceSelectPopover
										currencies={currencies}
										currentCurrency={currency}
										currentPriceType={priceType}
										filterCalculatedPrice={filterCalculatedPrice}
										referenceRect={referenceRect}
										width={405}
										onClose={this.handleClosePriceSelectPopover}
										onChange={this.handlePriceChange}
									/>
								) : null
							}
						</LayoutConstraint>
					</span>
				</div>
				<div className={styles.detailCell} />
			</div>
		);
	}
}
